import React from 'react';
import { Calendar, Clock, User, Star, CheckCircle, XCircle, ArrowRight, Lightbulb, TrendingUp, Building2, Shield, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

export const TestHarveyPage = () => {
  const { language } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": language === 'fr' 
      ? "Harvey IA Juridique : Test Complet et Avis pour les Avocats"
      : "Harvey Legal AI: Complete Review for Lawyers",
    "datePublished": "2026-01-19T10:00:00+01:00",
    "dateModified": "2026-01-19T10:00:00+01:00",
    "author": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "url": "https://gaius-tech.com"
    }
  };

  const seoTitle = language === 'fr' 
    ? "Harvey AI Avocat 2026 : Test Complet (Prix, Avis, Alternatives)"
    : "Harvey AI 2026: Complete Lawyer Review (Pricing, Pros, Cons)";

  const seoDescription = language === 'fr'
    ? "Test Harvey AI pour avocats : fonctionnalités, tarifs, avantages et limites. Comparatif avec Lexis Nexis IA et Doctrine."
    : "Harvey AI review for lawyers: features, pricing, pros and cons. Comparison with Lexis Nexis AI and Doctrine.";

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title={seoTitle}
          description={seoDescription}
          keywords="harvey ia juridique, test harvey avocat, avis harvey legal ai, ia générative avocats, intelligence artificielle cabinet avocat, harvey vs lexis nexis, harvey prix"
          canonicalUrl="https://gaius-tech.com/articles/test-harvey"
          alternateUrlFr="https://gaius-tech.com/articles/test-harvey"
          alternateUrlEn="https://gaius-tech.com/articles/test-harvey"
          structuredData={structuredData}
          pageType="article"
        />
        
        {/* Article Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">AI News</Badge>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Harvey AI pour Avocats : Test Complet 2026 (Prix, Avis et Alternatives)
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Analyse approfondie de l'IA juridique qui équipe Allen & Overy, Ashurst et les plus grands cabinets mondiaux. Fonctionnalités, retour d'expérience et verdict.
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>19 janvier 2026</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>18 min de lecture</span>
                </span>
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Équipe Gaius</span>
                </span>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=400&fit=crop"
              alt="Harvey IA juridique pour avocats"
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none">
            
            <p className="text-xl leading-relaxed mb-8">
              Harvey s'est imposé comme la référence mondiale de l'<strong>IA générative pour avocats</strong>. Développé en partenariat exclusif avec OpenAI et adopté par les cabinets du Magic Circle, cet outil représente aujourd'hui l'état de l'art en matière d'intelligence artificielle juridique. Nous l'avons testé pendant plusieurs semaines pour vous livrer une analyse complète et objective.
            </p>

            <div className="bg-primary/5 border-l-4 border-primary p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-bold text-lg mb-2">Chiffres clés Harvey 2026</h4>
                  <ul className="text-foreground/80 space-y-1 mb-0">
                    <li>• Plus de 15 000 avocats utilisateurs dans le monde</li>
                    <li>• Présent dans 25+ cabinets du Global 100</li>
                    <li>• Valorisation estimée à 700 millions de dollars</li>
                    <li>• Partenariat exclusif avec OpenAI pour le secteur juridique</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Qu'est-ce que Harvey AI ? L'IA juridique de référence des grands cabinets
            </h2>

            <p className="mb-6">
              Harvey est une plateforme d'<strong>intelligence artificielle générative</strong> spécialement conçue pour les professionnels du droit. Fondée en 2022 par Winston Weinberg (ancien avocat chez O'Melveny & Myers) et Gabriel Pereyra (ancien chercheur chez Google DeepMind), la startup a rapidement conquis les plus grands cabinets d'avocats internationaux.
            </p>

            <p className="mb-6">
              Contrairement aux solutions généralistes comme ChatGPT ou Claude, Harvey a été développé dès l'origine pour répondre aux exigences spécifiques de la pratique juridique : confidentialité absolue, précision terminologique, connaissance approfondie des systèmes juridiques et capacité à travailler sur des documents complexes.
            </p>

            <p className="mb-8">
              Le partenariat stratégique avec OpenAI permet à Harvey d'accéder aux modèles les plus avancés (GPT-4 et au-delà) tout en bénéficiant d'un fine-tuning spécifique sur des millions de documents juridiques. Cette combinaison unique explique la qualité remarquable des outputs.
            </p>

            {/* Section 2 */}
            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Test Harvey AI : fonctionnalités clés pour avocats
            </h2>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              1. Recherche juridique augmentée
            </h3>

            <p className="mb-6">
              Harvey excelle dans la recherche documentaire et jurisprudentielle. L'outil permet de formuler des requêtes en langage naturel et d'obtenir des synthèses structurées des sources pertinentes. Notre test a révélé une capacité impressionnante à identifier les décisions de principe et à les contextualiser.
            </p>

            <p className="mb-8">
              <strong>Exemple concret :</strong> Sur une question de responsabilité des administrateurs en droit anglais, Harvey a produit en moins de 2 minutes une synthèse de 15 pages identifiant les arrêts clés, les évolutions jurisprudentielles récentes et les points de divergence doctrinale. Un travail qui aurait nécessité 3 à 4 heures de recherche manuelle.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              2. Analyse et revue contractuelle
            </h3>

            <p className="mb-6">
              La fonctionnalité de contract review constitue l'un des points forts de Harvey. L'outil peut analyser des contrats de plusieurs centaines de pages, identifier les clauses problématiques, comparer avec des standards de marché et proposer des modifications.
            </p>

            <p className="mb-8">
              Nous avons testé Harvey sur un contrat d'acquisition de 200 pages. En 8 minutes, l'IA a produit un rapport détaillé identifiant 47 points d'attention, classés par niveau de risque, avec pour chacun une explication du problème et une suggestion de reformulation.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              3. Rédaction assistée et génération de documents
            </h3>

            <p className="mb-6">
              Harvey peut générer des premiers jets de documents juridiques : mémos, notes de synthèse, clauses contractuelles, correspondances. La qualité rédactionnelle est remarquable, avec un vocabulaire juridique précis et une structure professionnelle.
            </p>

            <p className="mb-8">
              Point notable : l'outil s'adapte au style de rédaction du cabinet. Après quelques utilisations, Harvey « apprend » les préférences rédactionnelles et produit des documents qui s'intègrent naturellement dans la production existante.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              4. Due diligence automatisée
            </h3>

            <p className="mb-8">
              En contexte M&A, Harvey permet d'accélérer considérablement les due diligences. L'IA peut traiter des centaines de documents, extraire les informations clés (clauses de changement de contrôle, engagements hors bilan, contentieux en cours) et produire des fiches de synthèse standardisées.
            </p>

            {/* Comparison Box */}
            <div className="bg-secondary/50 rounded-xl p-8 my-12">
              <h3 className="text-2xl font-bold mb-6 text-foreground">
                Harvey AI vs Doctrine vs Lexis : comparatif IA juridique
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6">
                  <h4 className="font-bold mb-3 flex items-center">
                    <Building2 className="w-5 h-5 text-primary mr-2" />
                    Harvey vs Lexis Nexis IA
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Harvey offre des capacités génératives supérieures, mais Lexis+ bénéficie de l'intégration native avec la base documentaire LexisNexis. Pour la recherche pure, Lexis reste pertinent ; pour l'analyse et la rédaction, Harvey domine.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-6">
                  <h4 className="font-bold mb-3 flex items-center">
                    <Building2 className="w-5 h-5 text-primary mr-2" />
                    Harvey vs Doctrine
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Doctrine excelle sur le droit français avec une couverture jurisprudentielle inégalée. Harvey est plus puissant sur l'analyse transversale et internationale. Les deux sont complémentaires.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Avis avocats : pourquoi Harvey AI impressionne
            </h2>

            <div className="space-y-6 my-8">
              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Qualité exceptionnelle des réponses</h4>
                  <p className="text-muted-foreground mt-1">
                    Le fine-tuning sur des millions de documents juridiques produit des outputs d'une précision remarquable. Le vocabulaire est juste, les raisonnements sont structurés, les nuances juridiques sont respectées.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Confidentialité garantie</h4>
                  <p className="text-muted-foreground mt-1">
                    Infrastructure dédiée, données non utilisées pour l'entraînement, certifications de sécurité (SOC 2 Type II). Les cabinets peuvent utiliser Harvey sur leurs dossiers les plus sensibles en toute sérénité.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Intégrations robustes</h4>
                  <p className="text-muted-foreground mt-1">
                    API complète, plugins pour Microsoft 365, intégration avec les principaux DMS (iManage, NetDocuments). Harvey s'insère naturellement dans les workflows existants.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Support et accompagnement</h4>
                  <p className="text-muted-foreground mt-1">
                    Équipe dédiée à l'onboarding, formation des utilisateurs, support réactif. Harvey investit dans la réussite de ses clients, pas seulement dans la technologie.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Limites de Harvey AI : prix, accès restreint et focus common law
            </h2>

            <div className="space-y-6 my-8">
              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Tarification élevée</h4>
                  <p className="text-muted-foreground mt-1">
                    Harvey pratique une tarification enterprise uniquement. Comptez plusieurs dizaines de milliers d'euros par an pour un cabinet de taille moyenne. L'investissement n'est rentable qu'au-delà d'un certain volume d'utilisation.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Accès restreint</h4>
                  <p className="text-muted-foreground mt-1">
                    Harvey privilégie les grands cabinets et les entreprises du Fortune 500. Les structures plus petites peinent à accéder à la plateforme, même en étant prêtes à payer.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Courbe d'apprentissage</h4>
                  <p className="text-muted-foreground mt-1">
                    Pour tirer le meilleur de Harvey, il faut maîtriser l'art du prompting juridique. Sans formation adéquate, les utilisateurs risquent de sous-exploiter l'outil.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Focus common law</h4>
                  <p className="text-muted-foreground mt-1">
                    Harvey a été développé principalement pour les systèmes de common law (US, UK). Si les performances sont excellentes en droit anglais ou américain, elles sont plus inégales sur le droit continental européen.
                  </p>
                </div>
              </div>
            </div>

            {/* Strategic Insight */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <Lightbulb className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-amber-800 font-bold text-lg mb-2">Notre conseil stratégique</h4>
                  <p className="text-amber-700 mb-0">
                    Pour les cabinets français, nous recommandons de combiner Harvey (pour les dossiers internationaux et l'analyse complexe) avec des solutions spécialisées droit français comme Jimini AI ou Doctrine. Cette approche hybride maximise la couverture tout en optimisant les coûts.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Retours d'expérience Harvey AI : témoignages d'avocats utilisateurs
            </h2>

            <p className="mb-6">
              Nous avons recueilli les témoignages de plusieurs avocats utilisant Harvey au quotidien. Le consensus est clair : l'outil transforme profondément la pratique, mais nécessite un investissement en formation.
            </p>

            <blockquote className="border-l-4 border-primary pl-6 italic my-8 text-lg">
              « Harvey a réduit de 40% le temps passé sur nos due diligences M&A. La qualité des analyses de risque est comparable à celle d'un collaborateur senior, mais en quelques minutes au lieu de plusieurs heures. »
              <footer className="text-sm text-muted-foreground mt-2 not-italic">— Associé, cabinet Magic Circle, Londres</footer>
            </blockquote>

            <blockquote className="border-l-4 border-primary pl-6 italic my-8 text-lg">
              « L'outil a changé notre façon de travailler sur les contrats complexes. Nous utilisons Harvey pour le premier niveau d'analyse, ce qui nous permet de nous concentrer sur les points vraiment stratégiques. »
              <footer className="text-sm text-muted-foreground mt-2 not-italic">— Counsel, cabinet américain, New York</footer>
            </blockquote>

            {/* Section 6 */}
            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Notre verdict final : Harvey AI vaut-il 1000€/mois pour un cabinet ?
            </h2>

            <div className="flex items-center space-x-3 my-6 p-4 bg-secondary/30 rounded-lg">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className={`w-8 h-8 ${i <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
              ))}
              <span className="ml-4 text-2xl font-bold">4/5</span>
            </div>

            <p className="mb-6">
              Harvey représente incontestablement l'excellence en matière d'<strong>IA générative pour avocats</strong>. La qualité des outputs, la sécurité des données et la profondeur fonctionnelle en font un outil de premier plan pour les cabinets qui peuvent y accéder.
            </p>

            <p className="mb-6">
              La note de 4/5 reflète les limitations d'accessibilité (prix, sélectivité) et le focus sur la common law qui peuvent freiner l'adoption par les cabinets français. Pour un cabinet international avec des dossiers cross-border, Harvey est un investissement qui se justifie pleinement.
            </p>

            <p className="mb-8">
              Pour les cabinets français travaillant principalement en droit national, nous recommandons d'explorer d'abord les alternatives locales (Jimini AI, Doctrine) avant de considérer Harvey comme complément pour les dossiers internationaux.
            </p>

            {/* Summary Box */}
            <div className="bg-primary/5 rounded-xl p-8 my-12">
              <h3 className="text-2xl font-bold mb-6 text-foreground">Récapitulatif Harvey</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-green-700 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" /> Points forts
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✓ Qualité exceptionnelle des outputs</li>
                    <li>✓ Sécurité et confidentialité garanties</li>
                    <li>✓ Partenariat OpenAI exclusif</li>
                    <li>✓ Intégrations complètes</li>
                    <li>✓ Support premium</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-red-700 mb-3 flex items-center">
                    <XCircle className="w-5 h-5 mr-2" /> Limitations
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✗ Prix élevé (enterprise only)</li>
                    <li>✗ Accès sélectif</li>
                    <li>✗ Focus common law</li>
                    <li>✗ Courbe d'apprentissage</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground">
                  <strong>Idéal pour :</strong> Cabinets internationaux, dossiers cross-border, opérations M&A complexes, due diligences volumineuses.
                </p>
              </div>
            </div>

          </div>

          {/* CTA Section */}
          <div className="mt-16 pt-8 border-t">
            <h3 className="text-2xl font-bold mb-6">Formations recommandées</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Zap className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Formation IA Générative Avocats</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Maîtrisez Harvey et les autres outils d'IA juridique pour transformer votre pratique.
                  </p>
                  <Button asChild>
                    <Link to="/training">
                      Découvrir <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Shield className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Audit IA Cabinet</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Identifiez les outils d'IA les plus adaptés à votre pratique et vos besoins.
                  </p>
                  <Button asChild variant="outline">
                    <Link to="/audit">
                      En savoir plus <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords="harvey legal ai, harvey lawyer review, generative ai lawyers, artificial intelligence law firm, harvey vs lexisnexis, harvey pricing"
        canonicalUrl="https://gaius-tech.com/articles/test-harvey"
        alternateUrlFr="https://gaius-tech.com/articles/test-harvey"
        alternateUrlEn="https://gaius-tech.com/articles/test-harvey"
        structuredData={structuredData}
        pageType="article"
      />
      
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">AI News</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Harvey AI for Lawyers: Complete 2026 Review (Pricing, Pros, Alternatives)
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              In-depth analysis of the legal AI powering Allen & Overy, Ashurst and the world's leading law firms. Features, real-world experience and verdict.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>January 19, 2026</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>18 min read</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          
          <p className="text-xl leading-relaxed mb-8">
            Harvey has established itself as the global reference for <strong>generative AI for lawyers</strong>. Developed in exclusive partnership with OpenAI and adopted by Magic Circle firms, this tool represents the state of the art in legal artificial intelligence. We tested it for several weeks to deliver a complete and objective analysis.
          </p>

          <div className="bg-primary/5 border-l-4 border-primary p-6 my-10 rounded-r-lg">
            <div className="flex items-start">
              <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-primary font-bold text-lg mb-2">Harvey Key Figures 2026</h4>
                <ul className="text-foreground/80 space-y-1 mb-0">
                  <li>• Over 15,000 lawyer users worldwide</li>
                  <li>• Present in 25+ Global 100 firms</li>
                  <li>• Estimated valuation of $700 million</li>
                  <li>• Exclusive OpenAI partnership for legal sector</li>
                </ul>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            What is Harvey? Introducing the Leading Legal AI
          </h2>

          <p className="mb-6">
            Harvey is a <strong>generative artificial intelligence</strong> platform specifically designed for legal professionals. Founded in 2022 by Winston Weinberg (former attorney at O'Melveny & Myers) and Gabriel Pereyra (former researcher at Google DeepMind), the startup quickly won over the world's largest international law firms.
          </p>

          <p className="mb-8">
            Unlike generalist solutions like ChatGPT or Claude, Harvey was developed from the outset to meet the specific requirements of legal practice: absolute confidentiality, terminological precision, deep knowledge of legal systems and the ability to work on complex documents.
          </p>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Key Features of Harvey for Lawyers
          </h2>

          <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
            1. Enhanced Legal Research
          </h3>

          <p className="mb-8">
            Harvey excels at document and case law research. The tool allows you to formulate queries in natural language and obtain structured summaries of relevant sources. Our testing revealed an impressive ability to identify landmark decisions and contextualize them.
          </p>

          <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
            2. Contract Analysis and Review
          </h3>

          <p className="mb-8">
            The contract review feature is one of Harvey's strong points. The tool can analyze contracts of several hundred pages, identify problematic clauses, compare with market standards and suggest modifications.
          </p>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Our Final Verdict on Harvey
          </h2>

          <div className="flex items-center space-x-3 my-6 p-4 bg-secondary/30 rounded-lg">
            {[1,2,3,4,5].map(i => (
              <Star key={i} className={`w-8 h-8 ${i <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
            ))}
            <span className="ml-4 text-2xl font-bold">4/5</span>
          </div>

          <p className="mb-8">
            Harvey unquestionably represents excellence in <strong>generative AI for lawyers</strong>. The quality of outputs, data security and functional depth make it a leading tool for firms that can access it. The 4/5 rating reflects accessibility limitations (price, selectivity) and the common law focus that may hinder adoption by continental European firms.
          </p>

        </div>

        <div className="mt-16 pt-8 border-t">
          <Button asChild>
            <Link to="/training">
              Discover our training programs <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};
